import App from 'app/app.js';

function StandardizedEnhancementService(
  PackageBundleService,
  ProductCatalogStore,
  SelectionTemplateDataStore,
  $q,
  OrderLineFactory,
  $injector
) {
  var that = this;

  /**
   * Update all subject personalization text to the nameOnText.
   * @param subject
   */
  this.syncSubjectEnhancements = (subject) => {
    if (!!subject && !!subject.nameOnText) {
      subject.isStandardizedPricing = true;

      [...(subject.packages || []), ...(subject.upgrades || [])].forEach((packageOrder) => {
        this.updateOrderableUpgrades(packageOrder, subject.nameOnText);
        this.updateOrderLineDetailUpgrades(packageOrder, subject.nameOnText);
      });
    }
  };

  this.updatePersonalizationSelection = (item, nameOnText) => {
    const selection = item?.orderLineItemSelections?.[0];

    if (selection && (item.type === 'PERSONALIZATION' || selection.selectionFieldName === 'nogoPersonalization')) {
      selection.value = nameOnText;
    }
  };

  /**
   * Iterate through the orderlines and swap the packages if the packages need updates. This is only done if the
   * job contains bundle packages, and the package is a base package or bundle package.
   * @param subject
   */
  this.syncPackageBundles = function (subject, editIndex) {
    if (!!subject) {
      if (this.packagesNeedUpdate(subject)) {
        //Sync packages
        R.forEachIndexed(function (orderline, index) {
          if (editIndex >= 0 && index === editIndex) {
            return; // skip because it was edited and is already updated
          }
          if (
            PackageBundleService.isBasePackage(orderline.orderable.skuCode) ||
            PackageBundleService.isBundlePackage(orderline.orderable.skuCode)
          ) {
            var newPackage = PackageBundleService.swapPackage(orderline, subject);
            that.regenerateSelectionTemplates(newPackage, subject).then(function () {
              subject.packages[index] = newPackage;
            });
          }
        })(subject.packages);
      }
    }
  };

  /**
   * Takes a newly generated package and the subject. It checks in the package orderables if
   * it needs a selection template loaded. If it does call the SelectionTemplateDataStore to get the data map.
   * Then load the data map into the package with the value of the subjects nameOnText.
   * @param _package
   * @param subject
   * @returns {*}
   */
  this.regenerateSelectionTemplates = function (_package, subject) {
    if (_package.orderable.upgrades) {
      var needsAdjustment = R.any(R.propEq('type', 'PERSONALIZATION'))(_package.orderable.upgrades);
      if (needsAdjustment) {
        var promises = R.map(function (upgrade) {
          if (upgrade.type === 'PERSONALIZATION') {
            if (!!upgrade.orderLineItemSelections) {
              return;
            }
            return SelectionTemplateDataStore.getSelectionTemplateDataMap(upgrade.templateName).then(
              function (data) {
                upgrade.selectionTemplateMap = data;
                upgrade.orderLineItemSelections = [
                  {
                    selectionFieldName: data.fields.field[0].name,
                    label: data.fields.field[0].label,
                    type: data.fields.field[0].labType,
                    value: subject.nameOnText
                  }
                ];
              }.bind(this)
            );
          }
          return $q.resolve();
        })(_package.orderable.upgrades);
        return $q.all(promises);
      }
      return $q.resolve();
    }
  };

  /**
   * Check all orderlines to see if the opt in's match the subjects opt outs. In this case I negate the opt out
   * so it matches the bundles hasRetouching. Ex: !optOut == hasRetouching. If its a base package check that the
   * opt outs are false. For bundle packages we must get the base package to check the truth map.
   * Return true if it needs updates, false if it does not. If there is no subject return false.
   * @param subject
   * @returns {boolean}
   */
  this.packagesNeedUpdate = function (subject) {
    if (!!subject) {
      return !R.all(function (orderLine) {
        if (PackageBundleService.isBasePackage(orderLine.orderable.skuCode)) {
          return (
            subject.optOutOfRetouchingEnhancements === false && subject.optOutOfPersonalizationEnhancements === false
          );
        } else {
          //if there is no base package listed then it is standard or basic, return early
          if (!orderLine.orderable.basePackage) {
            return true;
          }

          //TODO: Update the packages in backend to add the opt ins to bundle packages innately so we don't need the base package.
          var basePackage = that.getBasePackage(orderLine.orderable.basePackage);

          if (!!basePackage) {
            var bundleOptIns = R.find(R.propEq('skuCode', orderLine.orderable.skuCode))(basePackage.bundlePackages);
            if (!!bundleOptIns) {
              return (
                !subject.optOutOfRetouchingEnhancements === bundleOptIns.hasRetouching &&
                !subject.optOutOfPersonalizationEnhancements === bundleOptIns.hasPersonalization
              );
            } else {
              //if there is no bundle opt ins return that it does not need update
              return true;
            }
          } else {
            //if there is no base package return that it does not need update
            return true;
          }
        }
      })(subject.packages);
    }

    return false;
  };

  /**
   * Get the base package from the ProductCatalogStore and return it.
   * @param skuCode
   * @returns {*}
   */
  this.getBasePackage = function (skuCode) {
    return R.clone(R.find(R.propEq('skuCode', skuCode), ProductCatalogStore.getProductCatalog().packages));
  };

  /**
   * Find the personalization upgrade in the packages orderable upgrades and update the selection
   * template value to the name on text.
   * @param packageOrder: package inside a subject
   * @param nameOnText: nogo text of the subject
   */

  this.updateOrderableUpgrades = (packageOrder, nameOnText) => {
    const matched =
      (packageOrder.orderable.upgrades || []).find((upgrade) => upgrade.type === 'PERSONALIZATION') ||
      packageOrder.orderable;

    this.updatePersonalizationSelection(matched, nameOnText);
  };

  /**
   * Find the orderLineDetail with type personalization then update its selection template value with the
   * name on text.
   * @param packageOrder: package inside a subject
   * @param nameOnText: nogo text of a subject
   */
  this.updateOrderLineDetailUpgrades = (packageOrder, nameOnText) => {
    (packageOrder.orderLineDetails || []).forEach((orderLineDetail) => {
      this.updatePersonalizationSelection(orderLineDetail.item, nameOnText);
    });
  };

  /**
   * Cycle through all packages on a subject and make sure they match the name on text,
   * if they do not return false.
   * @param subject
   * @returns {boolean}
   */
  this.validateSubjectEnhancements = function (subject) {
    var valid = true;

    if (!!subject && !!subject.nameOnText) {
      R.forEach(function (packageOrder) {
        var matched = R.find(R.propEq('type', 'PERSONALIZATION'))(packageOrder.orderable.upgrades);

        if (!!matched && matched.orderLineItemSelections) {
          if (matched.orderLineItemSelections[0].value !== subject.nameOnText) {
            valid = false;
          }
        }

        R.forEach(function (orderLineDetail) {
          if (!!orderLineDetail.item.type && orderLineDetail.item.type === 'PERSONALIZATION') {
            if (
              !!orderLineDetail.item.orderLineItemSelections &&
              !!orderLineDetail.item.orderLineItemSelections[0].value
            ) {
              if (orderLineDetail.item.orderLineItemSelections[0].value !== subject.nameOnText) {
                valid = false;
              }
            }
          }
        })(packageOrder.orderLineDetails);
      })(subject.packages);
    }

    return valid;
  };

  /**
   * Will return the package with the highest level of enhancements
   *  assumes better enhancements will cause the package price to be higher
   * @param packageOrderLines - list of subject's package order lines
   * @returns winning package or null if none have enhancements
   */
  this.getWinningEnhancementPackage = function (packageOrderLines) {
    var packagesWithUpgrades = R.filter(function (pkg) {
      return pkg.orderable && pkg.orderable.upgrades && pkg.orderable.upgrades.length > 0;
    })(packageOrderLines);

    if (packagesWithUpgrades.length < 1) {
      return null;
    }

    var sortedPackages = R.sort(function (pkgA, pkgB) {
      return pkgA.orderable.salePrice - pkgB.orderable.salePrice;
    })(packagesWithUpgrades);

    return sortedPackages[sortedPackages.length - 1];
  };

  this.enhancementsAvailable = function (skuCode) {
    const shoppingCartStore = $injector.get('ShoppingCartStore');
    let _package = ProductCatalogStore.getProductCatalog().packages.find((pkg) => pkg.skuCode === skuCode);
    if (_package) {
      _package = structuredClone(_package); // Cloning the object using the spread operator
    }
    const selectedOrderLine = OrderLineFactory.createFromPackage(_package);
    const orderLineDetailList = selectedOrderLine.orderLineDetails.map((orderLineDetail) => {
      return orderLineDetail.item || {};
    });
    const availableEnhancements = shoppingCartStore.getAvailableEnhancements(orderLineDetailList);
    const basePackage = PackageBundleService.getBasePackage(skuCode);

    const param = basePackage?.skuCode || skuCode;
    let partialEnhancements = PackageBundleService.partialEnhancements(param);
    let isStandardizedPricingPackage = !!_package?.standardizedPricingContentZone;
    return (
      partialEnhancements ||
      (availableEnhancements.length > 0 && !isStandardizedPricingPackage) ||
      (isStandardizedPricingPackage && !partialEnhancements && _package.bundlePackages)
    );
  };
}

App.service('StandardizedEnhancementService', [
  'PackageBundleService',
  'ProductCatalogStore',
  'SelectionTemplateDataStore',
  '$q',
  'OrderLineFactory',
  '$injector',
  StandardizedEnhancementService
]);
